// Calculation identity and lifecycle from the register manager and the sheet
// workspace. Linked-source deletion is described only at the common guarded API
// boundary; family-specific cascade UI is intentionally not generalized.
import type { HelpArticle } from "../../types";
import { REGISTER_FIXTURE } from "../../../calculations/calcRegisterFixtures";

const sourceMark = REGISTER_FIXTURE.sheets.find((sheet) => sheet.id === "fj1")?.name ?? "FJ-1";

export const managingCalculationsArticle: HelpArticle = {
  id: "managing-calculations",
  title: "Renaming, duplicating & deleting calculations",
  description:
    "Manage a calculation without confusing its label with its identity: rename, make an independent copy, change a member section, and pass the permanent-delete guard.",
  tags: ["rename", "duplicate", "copy", "delete", "manage", "ellipsis", "group"],
  lastUpdated: "July 19, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A mark is editable; the calculation behind it has a stable id. The register manager keeps those two facts separate. Rename changes the label, Duplicate creates a new sheet, and Delete lives inside the worksheet behind its own confirmation.",
    },

    { kind: "heading", level: 2, text: "Open the calculation manager", id: "manager" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", "Overview", "⋯"] },
    {
      kind: "paragraph",
      text: "Use the ellipsis at the right edge of a table row or group row. The manager opens over the register and names the selected calculation in its header. Opening it does not enter the worksheet or commit a calculation change.",
    },
    {
      kind: "shot",
      shotId: "calc-manage-identity",
      caption: `${sourceMark}'s manager: identity first, then its manual-group assignment.`,
    },
    {
      kind: "paragraph",
      text: "Identity accepts a nonblank name. **Save** remains disabled until the trimmed value differs from the current name. A successful save updates the sheet in the loaded register and confirms the old and new names in a toast.",
    },
    {
      kind: "callout",
      variant: "note",
      title: "Rename-safe links",
      body: "Reaction links store the source and target sheet ids, not their marks. Renaming B-1 to G-1 changes what people read in the register; it does not replace the sheet or sever its load-path edges.",
    },
    {
      kind: "paragraph",
      text: "The Group select is active only in Manual organization. It offers every saved group plus Ungrouped. In Automatic mode it is disabled and explains that the calculator type owns the placement; change the organization mode on the overview if you need named project groups.",
    },

    { kind: "heading", level: 2, text: "Make an independent copy", id: "duplicate" },
    {
      kind: "paragraph",
      text: "Duplicate first fetches the full calculation, not the slim register row. It creates a new sheet with the same calculator id, complete input payload, and per-sheet design-method override. The source result is not copied as an opaque record: member calculations compute through the create endpoint, while a load calculation is created as an editable draft under the load-calculation contract.",
    },
    {
      kind: "shot",
      shotId: "calc-manage-actions",
      caption: "Member-specific section control above the common Open, Export, and Duplicate actions.",
    },
    {
      kind: "definition-list",
      items: [
        { term: "Name", definition: `The first copy of ${sourceMark} is ${sourceMark} copy. If that exists, the manager tries ${sourceMark} copy 2, then the next unused integer; comparisons ignore case.` },
        { term: "Inputs", definition: "The complete persisted input object is sent to the create endpoint, including calculator-specific rows and UI state held in that object." },
        { term: "Design method", definition: "A sheet-level ASD or LRFD override is copied. A null override remains null, so the duplicate follows the project default." },
        { term: "Group", definition: "In Manual mode the duplicate is assigned to the source's current group. In Automatic mode its calculator id determines placement." },
        { term: "Load links", definition: "Links are separate project edges and are not part of the create payload. A duplicate begins without the source sheet's inbound or outbound reaction links." },
      ],
    },
    {
      kind: "paragraph",
      text: "Duplicate does not navigate away. The new sheet is appended to the loaded register and a toast names it, so you can open the copy when you are ready. This is useful for a real design alternate because each copy can diverge; it is not a version history and no later edit synchronizes the pair.",
    },

    { kind: "heading", level: 2, text: "Change a member section from the register", id: "section" },
    {
      kind: "paragraph",
      text: "Wood member sheets add a Section area to the manager. It shows the current section and material, then **Change section** opens the same scored member database used inside the worksheet. The manager fetches full inputs, confirms that the member is complete enough to compute, and re-scores candidates against its current geometry and loads before applying a choice.",
    },
    {
      kind: "paragraph",
      text: "Registry-driven sheets and load calculations omit this area because their primary selections and inputs belong to their own workspaces. The common actions below it remain available: Open calculation, Export, and Duplicate.",
    },

    { kind: "heading", level: 2, text: "Delete from the worksheet", id: "delete" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", sourceMark, "Delete"] },
    {
      kind: "paragraph",
      text: "Delete is intentionally absent from the overview manager. Open the calculation, choose **Delete** in its header, and read the confirmation. Member sheets state that the calc sheet will be permanently removed; registry-driven and load-calculation sheets include the current mark in the same no-undo warning.",
    },
    {
      kind: "shot",
      shotId: "calc-delete-confirm",
      caption: "The shared destructive-action gate. Cancel receives initial focus; Delete requires a separate click.",
    },
    {
      kind: "paragraph",
      text: "The server refuses the ordinary delete when another calculation takes this sheet's reaction. Its conflict names the dependent sheets and says to unlink them first or use an unlink-and-delete path. The common safe workflow is to cancel, open each dependent, remove the inherited reaction link, and return only when deleting the source will no longer remove demand from another record.",
    },
    {
      kind: "paragraph",
      text: "After a successful deletion, the sheet disappears from the project state. Member and registry-driven workspaces route to the first remaining calculation, or to the empty overview when none remain. A load-calculation workspace returns directly to the overview. Deleting a manual group is different: that action moves its members to Ungrouped and deletes no calculations.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "No undo",
      body: "A calculation deletion removes the persisted sheet. Export the record first if the project needs an archival copy, and resolve reaction links before confirming so downstream demand does not disappear by surprise.",
    },

    { kind: "related", topics: ["calculation-register", "organizing-calculations", "linking-reactions"] },
  ],
};
